import {
  MongoAbility,
  PureAbility,
  RawRule,
  createMongoAbility,
  subject,
} from '@casl/ability';
import { PackRule, unpackRules } from '@casl/ability/extra';
import { createContextualCan } from '@casl/react';
import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDeepCompareMemo } from '../util/hooks';
import {
  ScratchpadBucket,
  SubscribeToUserPermissionsDocument,
  SubscribeToUserPermissionsSubscriptionResult,
} from './graphql/generated';
import { useApolloSharedSubscription } from './subscription/apollo-subscription.manager';
import { useCurrentUser } from './user.hooks';

export interface AuthorizationProviderProps {
  children: ReactNode;
}

export interface Permissions {
  create?: boolean;
  delete?: boolean;
  read?: boolean;
  update?: boolean;
}

export interface PermissionsOptions<T = Record<string, unknown>> {
  createObj?: T;
  deleteObj?: T;
  readObj?: T;
  updateObj?: T;
}

type LazyPermissionsReturnType = <T = Record<string, unknown>>(
  sub: string,
  defaultObj?: T,
  options?: PermissionsOptions<T>
) => Permissions;

interface PermissionTransformers {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [sub: string]: (obj: any) => Record<string, unknown>;
}

const transformers: PermissionTransformers = {
  scratchpad_bucket: (
    obj: Partial<ScratchpadBucket>
  ): Record<string, unknown> => {
    return {
      ...obj,
      scope: obj?.scope?.toLowerCase(),
    };
  },
};

export const AuthorizationContext =
  createContext<MongoAbility>(createMongoAbility());

const Can = createContextualCan(AuthorizationContext.Consumer);

const AuthorizationProvider = (
  props: AuthorizationProviderProps
): ReactElement | null => {
  const user = useCurrentUser();
  const [ability, setAbility] = useState<MongoAbility | undefined>();

  const { data } =
    useApolloSharedSubscription<SubscribeToUserPermissionsSubscriptionResult>({
      query: SubscribeToUserPermissionsDocument,
    });

  const createAbility = useCallback((permissions: PackRule<RawRule>[]) => {
    const rules = unpackRules(permissions);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newAbility = createMongoAbility(rules as any);
    setAbility(newAbility);
  }, []);

  useEffect(() => {
    if (user && user.permissions) {
      createAbility(user.permissions);
    }
  }, [user, createAbility]);

  useEffect(() => {
    if (data?.subscribeToUserPermissions) {
      createAbility(data?.subscribeToUserPermissions);
    }
  }, [data?.subscribeToUserPermissions, createAbility]);

  if (ability) {
    return (
      <AuthorizationContext.Provider value={ability}>
        {props.children}
      </AuthorizationContext.Provider>
    );
  }

  return null;
};

function checkPermission<T = Record<string, unknown>>(
  ability: PureAbility,
  action: string,
  sub: string,
  object?: T,
  field?: string
): boolean {
  if (object) {
    let permissionObj: T = { ...object };
    const transformer = transformers[sub];

    if (typeof transformer === 'function') {
      permissionObj = transformer(permissionObj) as T;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ability.can(action, subject(sub, permissionObj as any), field);
  } else {
    return ability.can(action, sub);
  }
}

function checkPermissions<T = Record<string, unknown>>(
  ability: MongoAbility,
  sub: string,
  defaultObj?: T,
  options?: PermissionsOptions<T>
): Permissions {
  return {
    create: checkPermission<T>(
      ability,
      'create',
      sub,
      options?.createObj ?? defaultObj
    ),
    delete: checkPermission<T>(
      ability,
      'delete',
      sub,
      options?.deleteObj ?? defaultObj
    ),
    read: checkPermission<T>(
      ability,
      'read',
      sub,
      options?.readObj ?? defaultObj
    ),
    update: checkPermission<T>(
      ability,
      'update',
      sub,
      options?.updateObj ?? defaultObj
    ),
  };
}

function useHasPermission<T = Record<string, unknown>>(
  action: string,
  sub: string,
  object?: T,
  field?: string
): boolean {
  const ability = useContext(AuthorizationContext);

  const permission = useDeepCompareMemo(() => {
    return checkPermission<T>(ability, action, sub, object, field);
  }, [ability, action, object, sub]);

  return permission;
}

function useHasFieldPermissions<T = Record<string, unknown>>(params: {
  action: string;
  sub: string;
  object?: T;
  fields: string[];
}): Record<string, boolean> {
  const { action, sub, object, fields } = params;
  const ability = useContext(AuthorizationContext);

  const fieldPermissions = useDeepCompareMemo(() => {
    const fieldPermissions: Record<string, boolean> = {};

    for (const field of fields) {
      fieldPermissions[field] = checkPermission<T>(
        ability,
        action,
        sub,
        object,
        field
      );
    }

    return fieldPermissions;
  }, [ability, action, object, sub]);

  return fieldPermissions;
}

function usePermissions<T = Record<string, unknown>>(
  sub: string,
  defaultObj?: T,
  options?: PermissionsOptions<T>
): Permissions {
  const ability = useContext(AuthorizationContext);

  const permissions = useDeepCompareMemo(() => {
    return checkPermissions(ability, sub, defaultObj, options);
  }, [ability, sub, defaultObj, options]);

  return permissions;
}

function usePermissionsLazy(): LazyPermissionsReturnType {
  const ability = useContext(AuthorizationContext);

  const getPermissions = useCallback<LazyPermissionsReturnType>(
    function <T = Record<string, unknown>>(
      sub: string,
      defaultObj?: T,
      options?: PermissionsOptions<T>
    ) {
      return checkPermissions(ability, sub, defaultObj, options);
    },
    [ability]
  );

  return getPermissions;
}

export {
  AuthorizationProvider,
  Can,
  useHasPermission,
  useHasFieldPermissions,
  usePermissions,
  usePermissionsLazy,
};
